import { HttpTypes } from "@medusajs/types"

export type PromotionTargetRule = {
  attribute?: string
  operator?: string
  values?: { value?: string }[]
}

export type Promotion = {
  id: string
  code?: string
  application_method?: {
    type?: "fixed" | "percentage"
    value?: number
    currency_code?: string
    target_type?: string
    target_rules?: PromotionTargetRule[]
  } | null
  campaign?: {
    starts_at?: Date | string | null
    ends_at?: Date | string | null
  } | null
}

/**
 * ESPEJO del backend: `backend/src/lib/promotion-utils.ts` (matchesRule).
 * Si cambias la lógica aquí, actualiza también el backend (y viceversa).
 */
export function matchesRule(
  rule: PromotionTargetRule,
  product: HttpTypes.StoreProduct
): boolean {
  const vals = rule.values?.map((v) => v.value).filter(Boolean) ?? []
  if (!vals.length) return false
  const attr = rule.attribute ?? ""
  switch (attr) {
    case "products.id":
    case "product_id":
    case "items.product.id":
      return vals.includes(product.id)
    case "products.tags.id":
    case "product.tags":
    case "items.product.tags.id":
      return (product.tags ?? []).some((t: any) => vals.includes(t.id))
    case "products.collection_id":
    case "product.collection":
    case "items.product.collection_id":
      return vals.includes(product.collection_id as string)
    case "products.type_id":
    case "product.type":
    case "items.product.type_id":
      return vals.includes(product.type_id as string)
    default:
      return false
  }
}

/**
 * ESPEJO del backend: `backend/src/lib/promotion-utils.ts` (findBestDiscount).
 * Aplica las promociones automáticas activas sobre un precio con impuestos
 * incluidos y devuelve el mejor precio descontado (2 decimales), o `null`
 * si ninguna promoción aplica.
 */
export function findBestDiscount(
  product: HttpTypes.StoreProduct,
  promotions: Promotion[],
  originalPriceWithTax: number,
  currencyCode: string
): number | null {
  let bestPrice = originalPriceWithTax
  const now = new Date()

  for (const promo of promotions) {
    if (promo.campaign?.starts_at && new Date(promo.campaign.starts_at) > now) continue
    if (promo.campaign?.ends_at && new Date(promo.campaign.ends_at) < now) continue

    const method = promo.application_method
    if (!method || method.target_type !== "items") continue

    const targetRules = method.target_rules ?? []
    if (targetRules.length > 0) {
      const matches = targetRules.some((rule) => matchesRule(rule, product))
      if (!matches) continue
    }

    let discountedPrice = originalPriceWithTax
    if (method.type === "percentage" && method.value) {
      discountedPrice = originalPriceWithTax * (1 - method.value / 100)
    } else if (method.type === "fixed" && method.value) {
      if (method.currency_code?.toLowerCase() === currencyCode.toLowerCase()) {
        discountedPrice = originalPriceWithTax - method.value
      }
    }

    if (discountedPrice < bestPrice) {
      bestPrice = discountedPrice
    }
  }

  return bestPrice < originalPriceWithTax ? Math.round(bestPrice * 100) / 100 : null
}

/**
 * Precio descontado de un producto, con dos fuentes:
 *  1. `product.metadata.salesPriceWithIva` — precalculado en el backend al
 *     indexar en MeiliSearch (hits del listado y del buscador). Sin cálculo.
 *  2. Si no existe, `findBestDiscount` sobre las promociones de Medusa
 *     (productos servidos por la API de Medusa: página de producto, home,
 *     relacionados).
 *
 * Devuelve `null` si no aplica ningún descuento.
 */
export function getDiscountedPrice(
  product: HttpTypes.StoreProduct,
  promotions: Promotion[],
  basePriceWithTax: number,
  currencyCode: string
): number | null {
  const meiliSales = (product.metadata as any)?.salesPriceWithIva
  if (typeof meiliSales === "number" && meiliSales > 0 && meiliSales < basePriceWithTax) {
    return meiliSales
  }
  return findBestDiscount(product, promotions, basePriceWithTax, currencyCode)
}
